import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * The app's one confirmation surface for a destructive action.
 *
 * Task 863 widened it so the delete lane could stand on it rather than add a
 * fourth bespoke modal:
 *
 * - **`message` is a ReactNode.** A delete has to say what goes and what stays,
 *   which is a short list, not a sentence. A string is still accepted and still
 *   renders as the paragraph it always did — the three existing callers pass
 *   one — but a node is rendered as itself, never nested inside that paragraph,
 *   because a `<ul>` or `<div>` inside a `<p>` is invalid HTML the browser
 *   silently re-parents.
 * - **`confirmText` is the type-the-name speed bump** the account slug change
 *   already uses (`SlugChangeConfirmDialog`). Reserved for the deletes that
 *   remove a public address; absent, Confirm is live immediately as before.
 * - **Escape actually works.** The old handler was `onKeyDown` on a div nothing
 *   ever focused, so it never fired. The listener is now on the document and
 *   the dialog takes focus on open — the type-to-confirm input when there is
 *   one, since that is where the User has to type next.
 * - **It renders through a portal to `document.body`** (task 1198). Rendered in
 *   place, a dialog opened from a table cell (the AI Services row menu) inherited
 *   the cell's `text-right whitespace-nowrap`. The panel also resets both, for
 *   any ancestor style a portal would not escape. React still bubbles the
 *   dialog's synthetic events along the React tree, so a caller's click guard
 *   around it (task 1161) keeps working.
 */
interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  confirmStyle?: 'danger' | 'primary';
  isLoading?: boolean;
  /**
   * When set, the User must type this exact text before Confirm enables. Use
   * for a delete that frees a public address; everything else should not spend
   * the User's keystrokes.
   */
  confirmText?: string;
  /**
   * Drops the confirm button entirely (task 864). For a dialog that exists to
   * REFUSE — a delete a live AI Service depends on — where offering a disabled
   * Delete would read as "not yet" rather than "not until you repoint that
   * service". Cancel becomes the only way out, so name it accordingly.
   */
  hideConfirm?: boolean;
  /** Label for the dismiss button. "Close" reads better than "Cancel" on a refusal. */
  cancelLabel?: string;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  confirmStyle = 'primary',
  isLoading = false,
  confirmText,
  hideConfirm = false,
  cancelLabel = 'Cancel',
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // A fresh dialog starts with an empty box: reopening after a cancel must not
  // arrive with Confirm already armed from the previous attempt.
  useEffect(() => {
    if (isOpen) setTyped('');
  }, [isOpen]);

  // Escape at the document, not on the backdrop. Nothing focuses a bare div, so
  // the previous `onKeyDown` on the backdrop was unreachable.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, isLoading, onClose]);

  // Focus on open: the input when the User has to type, otherwise the panel, so
  // the keyboard is inside the dialog either way.
  useEffect(() => {
    if (!isOpen) return;
    if (inputRef.current) inputRef.current.focus();
    else panelRef.current?.focus();
  }, [isOpen, hideConfirm]);

  if (!isOpen) return null;
  // No document to portal into (a non-browser render): render nothing.
  if (typeof document === 'undefined') return null;

  const requiresTyping =
    !hideConfirm && typeof confirmText === 'string' && confirmText.length > 0;
  const typedMatches = !requiresTyping || typed.trim() === confirmText;
  const confirmDisabled = isLoading || !typedMatches;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl focus:outline-none text-left whitespace-normal"
      >
        {/* Header with icon */}
        <div className="flex items-start mb-4">
          {confirmStyle === 'danger' && (
            <div className="flex-shrink-0 mr-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-500" aria-hidden="true" />
            </div>
          )}
          <div className="flex-1">
            <h3 id="confirm-dialog-title" className="text-lg font-medium text-gray-900 dark:text-white">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-shrink-0 ml-3 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close dialog"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Message. A string keeps the paragraph it has always had; a node
            renders as itself, because block elements inside a <p> are invalid. */}
        <div className="mb-6 text-sm text-gray-700 dark:text-gray-300">
          {typeof message === 'string' ? <p>{message}</p> : message}
        </div>

        {requiresTyping && (
          <div className="mb-6">
            <label
              htmlFor="confirm-dialog-text-input"
              data-testid="confirm-dialog-text-label"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Type <span className="font-mono font-semibold">{confirmText}</span> to confirm
            </label>
            <input
              id="confirm-dialog-text-input"
              ref={inputRef}
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={isLoading}
              data-testid="confirm-dialog-text-input"
              autoComplete="off"
              className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm sm:text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelLabel}
          </button>
          {!hideConfirm && (
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirmDisabled}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center
              ${
                confirmStyle === 'danger'
                  ? 'bg-red-600 dark:bg-red-700 hover:bg-red-700 dark:hover:bg-red-800 focus:ring-red-500'
                  : 'bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-800 focus:ring-blue-500'
              }`}
          >
            {isLoading && (
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isLoading ? 'Processing...' : confirmLabel}
          </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
